import React, { useState, useEffect } from 'react';
import SEOHead from '@/components/SEOHead';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, MapPin, Users, Search, ArrowLeft, X, CheckSquare } from 'lucide-react';
import { TeamLogo } from '@/components/games/TeamLogo';
import { gamesService, Game } from '@/services/gamesService';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { EnhancedGameDetailsModal } from '@/components/games/EnhancedGameDetailsModal';
import { useGameRealTimeManager } from '@/hooks/useGameRealTimeManager';
import { PlayerJoinRequestButton } from '@/components/games/PlayerJoinRequestButton';

export default function Games() {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [games, setGames] = useState<Game[]>([]);
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [sportFilter, setSportFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [gameTypeFilter, setGameTypeFilter] = useState('all');
  const [gameSourceFilter, setGameSourceFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('');
  const [venueFilter, setVenueFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sports, setSports] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [showGameDetails, setShowGameDetails] = useState(false);
  
  // Enhanced filter states
  const [countryFilter, setCountryFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('');
  const [stateProvinceFilter, setStateProvinceFilter] = useState('all');
  const [zipCodeFilter, setZipCodeFilter] = useState('');
  const [timeFilter, setTimeFilter] = useState('all');
  const [venueTypeFilter, setVenueTypeFilter] = useState('all');
  const [needsScorekeeperFilter, setNeedsScorekeeperFilter] = useState(false);
  const [specificDateFilter, setSpecificDateFilter] = useState('');
  
  // Filter data states
  const [locations, setLocations] = useState<any[]>([]);
  const [venueTypes, setVenueTypes] = useState<any[]>([]);

  const user = supabaseUser || internalUser;

  const loadGames = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allGames = await gamesService.getAllGames();
      setGames(allGames);
    } catch (error) {
      console.error('Error loading games:', error);
      setError('Failed to load games. Please try again.');
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSports = async () => {
    try {
      const { data, error } = await supabase
        .from('available_sports')
        .select('*')
        .order('sport_name');

      if (error) throw error;
      setSports(data || []);
    } catch (error) {
      console.error('Error loading sports:', error);
    }
  };

  const loadLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('is_active', true)
        .order('country, state_province, city');

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadVenueTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('venue_types')
        .select('*')
        .eq('is_active', true)
        .order('type_name');

      if (error) throw error;
      setVenueTypes(data || []);
    } catch (error) {
      console.error('Error loading venue types:', error);
    }
  };

  useEffect(() => {
    loadGames();
    loadSports();
    loadLocations();
    loadVenueTypes();
  }, []);

  // Real-time updates
  useGameRealTimeManager({
    userId: user?.id,
    onGameUpdate: loadGames,
    onGameStatusUpdate: loadGames
  });

  useEffect(() => {
    filterGames();
  }, [games, searchTerm, statusFilter, locationFilter, sportFilter, dateFilter, gameTypeFilter, gameSourceFilter, teamFilter, venueFilter, countryFilter, cityFilter, stateProvinceFilter, zipCodeFilter, timeFilter, venueTypeFilter, needsScorekeeperFilter, specificDateFilter]);

  const filterGames = () => {
    let filtered = games;

    // General search term filter (games, venues, teams, game codes)
    if (searchTerm) {
      filtered = filtered.filter(game => 
        game.game_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        game.venue_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        game.venue_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        game.home_team_label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        game.away_team_label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        game.game_code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Location-specific filter (venue name, address, field location)
    if (locationFilter) {
      filtered = filtered.filter(game => 
        game.venue_name?.toLowerCase().includes(locationFilter.toLowerCase()) ||
        game.venue_address?.toLowerCase().includes(locationFilter.toLowerCase())
      );
    }

    // Venue-specific filter
    if (venueFilter) {
      filtered = filtered.filter(game => 
        game.venue_name?.toLowerCase().includes(venueFilter.toLowerCase())
      );
    }

    // Team-specific filter (search by team names)
    if (teamFilter) {
      filtered = filtered.filter(game => 
        game.home_team_label.toLowerCase().includes(teamFilter.toLowerCase()) ||
        game.away_team_label.toLowerCase().includes(teamFilter.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(game => 
        game.game_status === statusFilter || game.confirmation_status === statusFilter
      );
    }

    // Sport filter
    if (sportFilter !== 'all') {
      filtered = filtered.filter(game => game.sport_id?.toString() === sportFilter);
    }

    // Specific date filter (takes precedence over general date filter)
    if (specificDateFilter) {
      filtered = filtered.filter(game => game.game_date === specificDateFilter);
    } else if (dateFilter !== 'all') {
      // General date filter
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      
      filtered = filtered.filter(game => {
        const gameDate = game.game_date;
        
        switch (dateFilter) {
          case 'today':
            return gameDate === todayStr;
          case 'tomorrow':
            const tomorrow = new Date(today);
            tomorrow.setDate(today.getDate() + 1);
            return gameDate === tomorrow.toISOString().split('T')[0];
          case 'this_week':
            const weekFromNow = new Date(today);
            weekFromNow.setDate(today.getDate() + 7);
            return gameDate >= todayStr && gameDate <= weekFromNow.toISOString().split('T')[0];
          case 'this_month':
            const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            return gameDate >= todayStr && gameDate <= endOfMonth.toISOString().split('T')[0];
          case 'upcoming':
            return gameDate >= todayStr;
          case 'past':
            return gameDate < todayStr;
          default:
            return true;
        }
      });
    }

    // Game type filter (using game_name patterns)
    if (gameTypeFilter !== 'all') {
      filtered = filtered.filter(game => {
        const gameName = game.game_name.toLowerCase();
        return gameName.includes(gameTypeFilter.toLowerCase());
      });
    }

    // Game source filter (regular games vs venue games)
    if (gameSourceFilter !== 'all') {
      if (gameSourceFilter === 'venue_games') {
        // Games that have field_location or were posted by venue
        filtered = filtered.filter(game => 
          game.venue_name?.includes('Venue') || 
          game.created_by !== game.home_team_id?.toString()
        );
      } else if (gameSourceFilter === 'regular_games') {
        // Games from the regular games table
        filtered = filtered.filter(game => 
          game.game_timezone && 
          !game.venue_name?.includes('Venue')
        );
      }
    }

    // Country filter
    if (countryFilter && countryFilter !== 'all') {
      filtered = filtered.filter(game => 
        game.venue_address?.toLowerCase().includes(countryFilter.toLowerCase())
      );
    }

    // City filter
    if (cityFilter) {
      filtered = filtered.filter(game => 
        game.venue_address?.toLowerCase().includes(cityFilter.toLowerCase()) ||
        game.venue_name?.toLowerCase().includes(cityFilter.toLowerCase())
      );
    }

    // State/Province filter
    if (stateProvinceFilter && stateProvinceFilter !== 'all') {
      filtered = filtered.filter(game => 
        game.venue_address?.toLowerCase().includes(stateProvinceFilter.toLowerCase())
      );
    }

    // Zip code filter
    if (zipCodeFilter) {
      filtered = filtered.filter(game => 
        game.venue_address?.includes(zipCodeFilter)
      );
    }

    // Time filter
    if (timeFilter !== 'all') {
      filtered = filtered.filter(game => {
        const gameTime = game.game_time;
        const hour = parseInt(gameTime.split(':')[0]);
        
        switch (timeFilter) {
          case 'morning':
            return hour >= 6 && hour < 12;
          case 'afternoon':
            return hour >= 12 && hour < 18;
          case 'evening':
            return hour >= 18 && hour < 24;
          case 'night':
            return hour >= 0 && hour < 6;
          default:
            return true;
        }
      });
    }

    // Venue type filter
    if (venueTypeFilter !== 'all') {
      filtered = filtered.filter(game => 
        game.venue_name?.toLowerCase().includes(venueTypeFilter.toLowerCase())
      );
    }

    // Need scorekeeper filter
    if (needsScorekeeperFilter) {
      filtered = filtered.filter(game => {
        // For now, show all games when this filter is enabled
        // This can be enhanced later with actual scorekeeper participant data
        // You may want to query game_participants table to check for scorekeeper roles
        return true;
      });
    }

    setFilteredGames(filtered);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "destructive" | "outline" | "secondary"> = {
      pending: "outline",
      confirmed: "default",
      in_progress: "secondary",
      completed: "secondary",
      cancelled: "destructive",
      forfeited: "destructive"
    };

    return (
      <Badge variant={variants[status] || "outline"} className="capitalize">
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const getSportName = (sportId?: number) => {
    if (!sportId) return 'N/A';
    const sport = sports.find(s => s.id === sportId);
    return sport?.sport_name || 'Unknown Sport';
  };

  const formatGameTime = (time: string, timezone?: string) => {
    try {
      // Parse the time (assuming it's in HH:mm format)
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      
      // Convert to CST (UTC-6) - assuming most times are in UTC or EST
      // For simplicity, we'll show the time in CST format
      const timeString = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Chicago' // CST timezone
      });
      
      // Always show CST
      return `${timeString} CST`;
    } catch (error) {
      // Fallback to original time if parsing fails
      return `${time} CST`;
    }
  };

  const handleGameClick = (game: Game) => {
    console.log('Games page: Game clicked:', game.game_name);
    console.log('Games page: Setting selectedGame and showGameDetails to true');
    setSelectedGame(game);
    setShowGameDetails(true);
  };


  if (loading) {
    return (
    <div className="flex flex-col min-h-screen bg-black">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className="flex-grow flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </main>
      
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-black">
      <SEOHead title="Games" description="Discover and join live sports games near you on Players Lockeroom." path="/games" />
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className={`flex-grow ${isMobile ? 'pt-16 pb-16' : 'pt-32'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-white hover:text-primary hover:bg-gray-800"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">Games</h1>
            <p className="text-gray-300">Discover and join games in your area</p>
          </div>

          {/* Search & Filter Teams Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Search className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold text-primary">Search & Filter Games</h2>
            </div>
          </div>

           {/* Search and Filters */}
           <div className="mb-6 space-y-4">
             {/* Primary Search */}
             <div className="flex flex-col gap-4">
               <div className="relative">
                 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                 <Input
                   placeholder="Search by team name, sport, or location..."
                   value={searchTerm}
                   onChange={(e) => setSearchTerm(e.target.value)}
                   className="pl-10"
                 />
               </div>

               {/* Expandable Filters */}
               <div className="space-y-3">
                 {/* Quick Filters Row */}
                 <div className="flex flex-wrap gap-2">
                   {/* Date Filter */}
                   <div className="flex-1 min-w-[200px] lg:max-w-[250px]">
                     <Input
                       type="date"
                       value={specificDateFilter}
                       onChange={(e) => setSpecificDateFilter(e.target.value)}
                       className="w-full text-sm"
                       placeholder="Select date"
                     />
                   </div>

                   {/* Sport Filter */}
                   <div className="flex-1 min-w-[150px] lg:max-w-[200px]">
                     <Select value={sportFilter} onValueChange={setSportFilter}>
                       <SelectTrigger className="text-sm">
                         <SelectValue placeholder="All Sports" />
                       </SelectTrigger>
                       <SelectContent className="max-h-60 overflow-y-auto">
                         <SelectItem value="all">All Sports</SelectItem>
                         {sports.map((sport) => (
                           <SelectItem key={sport.id} value={sport.id.toString()}>
                             {sport.sport_name}
                           </SelectItem>
                         ))}
                       </SelectContent>
                     </Select>
                   </div>

                   {/* Status Filter */}
                   <div className="flex-1 min-w-[150px] lg:max-w-[200px]">
                     <Select value={statusFilter} onValueChange={setStatusFilter}>
                       <SelectTrigger className="text-sm">
                         <SelectValue placeholder="All Statuses" />
                       </SelectTrigger>
                       <SelectContent>
                         <SelectItem value="all">All Games</SelectItem>
                         <SelectItem value="confirmed">Confirmed</SelectItem>
                         <SelectItem value="cancelled">Cancelled</SelectItem>
                         <SelectItem value="pending">Pending</SelectItem>
                         <SelectItem value="in_progress">In Progress</SelectItem>
                         <SelectItem value="completed">Completed</SelectItem>
                       </SelectContent>
                     </Select>
                   </div>

                   {/* More Filters Toggle */}
                   <Button
                     variant="outline"
                     size="sm"
                     onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                     className="flex items-center gap-2 min-w-[120px]"
                   >
                     {showAdvancedFilters ? (
                       <>
                         <X className="h-4 w-4" />
                         Less Filters
                       </>
                     ) : (
                       <>
                         <Search className="h-4 w-4" />
                         More Filters
                       </>
                     )}
                   </Button>
                 </div>

                 {/* Advanced Filters - Collapsible */}
                 {showAdvancedFilters && (
                   <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                       {/* Time Filter */}
                       <div className="space-y-2">
                         <label className="text-sm font-medium text-foreground">Time</label>
                         <Select value={timeFilter} onValueChange={setTimeFilter}>
                           <SelectTrigger className="text-sm">
                             <SelectValue placeholder="All Times" />
                           </SelectTrigger>
                           <SelectContent>
                             <SelectItem value="all">All Times</SelectItem>
                             <SelectItem value="morning">Morning (6AM-12PM)</SelectItem>
                             <SelectItem value="afternoon">Afternoon (12PM-6PM)</SelectItem>
                             <SelectItem value="evening">Evening (6PM-12AM)</SelectItem>
                             <SelectItem value="night">Night (12AM-6AM)</SelectItem>
                           </SelectContent>
                         </Select>
                       </div>

                       {/* Venue Filter */}
                       <div className="space-y-2">
                         <label className="text-sm font-medium text-foreground">Venue</label>
                         <Input
                           placeholder="Search venues..."
                           value={venueFilter}
                           onChange={(e) => setVenueFilter(e.target.value)}
                           className="text-sm"
                         />
                       </div>

                       {/* Country Filter */}
                       <div className="space-y-2">
                         <label className="text-sm font-medium text-foreground">Country</label>
                         <Select value={countryFilter} onValueChange={setCountryFilter}>
                           <SelectTrigger className="text-sm">
                             <SelectValue placeholder="All Countries" />
                           </SelectTrigger>
                           <SelectContent>
                             <SelectItem value="all">All Countries</SelectItem>
                             <SelectItem value="Canada">Canada</SelectItem>
                             <SelectItem value="United States">United States</SelectItem>
                             <SelectItem value="Mexico">Mexico</SelectItem>
                             <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                             <SelectItem value="Germany">Germany</SelectItem>
                             <SelectItem value="France">France</SelectItem>
                             <SelectItem value="Australia">Australia</SelectItem>
                             <SelectItem value="Japan">Japan</SelectItem>
                             <SelectItem value="Brazil">Brazil</SelectItem>
                             <SelectItem value="India">India</SelectItem>
                           </SelectContent>
                         </Select>
                       </div>

                       {/* City Filter */}
                       <div className="space-y-2">
                         <label className="text-sm font-medium text-foreground">City</label>
                         <Input
                           placeholder="Enter city name..."
                           value={cityFilter}
                           onChange={(e) => setCityFilter(e.target.value)}
                           className="text-sm"
                         />
                       </div>

                       {/* State/Province Filter */}
                       <div className="space-y-2">
                         <label className="text-sm font-medium text-foreground">State/Province</label>
                         <Select value={stateProvinceFilter} onValueChange={setStateProvinceFilter}>
                           <SelectTrigger className="text-sm">
                             <SelectValue placeholder="All States/Provinces" />
                           </SelectTrigger>
                           <SelectContent>
                             <SelectItem value="all">All States/Provinces</SelectItem>
                             {countryFilter === 'United States' && (
                               <>
                                 <SelectItem value="California">California</SelectItem>
                                 <SelectItem value="Texas">Texas</SelectItem>
                                 <SelectItem value="Florida">Florida</SelectItem>
                                 <SelectItem value="New York">New York</SelectItem>
                                 <SelectItem value="Illinois">Illinois</SelectItem>
                               </>
                             )}
                             {countryFilter === 'Canada' && (
                               <>
                                 <SelectItem value="Ontario">Ontario</SelectItem>
                                 <SelectItem value="Quebec">Quebec</SelectItem>
                                 <SelectItem value="British Columbia">British Columbia</SelectItem>
                                 <SelectItem value="Alberta">Alberta</SelectItem>
                                 <SelectItem value="Manitoba">Manitoba</SelectItem>
                               </>
                             )}
                             {!countryFilter || countryFilter === 'all' && (
                               <>
                                 <SelectItem value="California">California</SelectItem>
                                 <SelectItem value="Texas">Texas</SelectItem>
                                 <SelectItem value="Florida">Florida</SelectItem>
                                 <SelectItem value="Ontario">Ontario</SelectItem>
                                 <SelectItem value="Quebec">Quebec</SelectItem>
                               </>
                             )}
                           </SelectContent>
                         </Select>
                       </div>

                       {/* Need Score Keeper Filter */}
                       <div className="space-y-2">
                         <label className="text-sm font-medium text-foreground">Need Score Keeper?</label>
                         <div className="flex items-center space-x-2 mt-2">
                           <input
                             type="checkbox"
                             id="scorekeeper-needed"
                             checked={needsScorekeeperFilter}
                             onChange={(e) => setNeedsScorekeeperFilter(e.target.checked)}
                             className="rounded border-input w-4 h-4"
                           />
                           <label htmlFor="scorekeeper-needed" className="text-sm text-foreground cursor-pointer">
                             Show only games needing scorekeepers
                           </label>
                         </div>
                       </div>
                     </div>

                     {/* Clear All Filters */}
                     <div className="mt-4 pt-4 border-t border-border/50">
                       <Button
                         variant="outline"
                         size="sm"
                         onClick={() => {
                           setSearchTerm('');
                           setStatusFilter('all');
                           setLocationFilter('');
                           setSportFilter('all');
                           setDateFilter('all');
                           setGameTypeFilter('all');
                           setGameSourceFilter('all');
                           setTeamFilter('');
                           setVenueFilter('');
                           setCountryFilter('all');
                           setCityFilter('');
                           setStateProvinceFilter('all');
                           setZipCodeFilter('');
                           setTimeFilter('all');
                           setVenueTypeFilter('all');
                           setNeedsScorekeeperFilter(false);
                           setSpecificDateFilter('');
                         }}
                         className="flex items-center gap-2"
                       >
                         <X className="h-4 w-4" />
                         Clear All Filters
                       </Button>
                     </div>
                   </div>
                 )}
               </div>
            </div>

           {/* Quick Filter Chips */}
          <div className="flex flex-wrap gap-2">
            {searchTerm && (
              <Badge variant="secondary" className="px-3 py-1">
                Search: {searchTerm}
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-4 w-4 p-0"
                  onClick={() => setSearchTerm('')}
                >
                  ×
                </Button>
              </Badge>
            )}
            {locationFilter && (
              <Badge variant="secondary" className="px-3 py-1">
                Location: {locationFilter}
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-4 w-4 p-0"
                  onClick={() => setLocationFilter('')}
                >
                  ×
                </Button>
              </Badge>
            )}
            {statusFilter !== 'all' && (
              <Badge variant="secondary" className="px-3 py-1">
                Status: {statusFilter}
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-4 w-4 p-0"
                  onClick={() => setStatusFilter('all')}
                >
                  ×
                </Button>
              </Badge>
            )}
            {sportFilter !== 'all' && (
              <Badge variant="secondary" className="px-3 py-1">
                Sport: {getSportName(parseInt(sportFilter))}
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2 h-4 w-4 p-0"
                  onClick={() => setSportFilter('all')}
                >
                  ×
                </Button>
              </Badge>
            )}
          </div>
        </div>

        {/* Games Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredGames.map((game) => (
            <Card 
              key={game.id} 
              className="bg-card border-border hover:border-primary/20 transition-all duration-200 cursor-pointer"
              onClick={() => handleGameClick(game)}
            >
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg text-foreground">{game.game_name}</CardTitle>
                    <CardDescription className="mt-1 text-muted-foreground">
                      {getSportName(game.sport_id)}
                    </CardDescription>
                  </div>
                  {getStatusBadge(game.game_status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {game.game_code && (
                    <div className="bg-muted/20 p-2 rounded-lg">
                      <div className="text-xs text-muted-foreground">Game Code:</div>
                      <div className="font-mono font-bold text-sm text-foreground">{game.game_code}</div>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4 mr-2 text-primary" />
                    {format(new Date(game.game_date), 'MMM d, yyyy')}
                  </div>
                   <div className="flex items-center text-sm text-muted-foreground">
                     <Clock className="h-4 w-4 mr-2 text-primary" />
                     {formatGameTime(game.game_time, game.game_timezone)}
                   </div>
                   <div className="flex items-center text-sm text-muted-foreground">
                     <span className="font-medium mr-2">Sport:</span>
                     <span className="text-foreground">{getSportName(game.sport_id)}</span>
                   </div>
                   {game.venue_name && (
                     <div className="flex items-start text-sm text-muted-foreground">
                       <MapPin className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-primary" />
                       <div>
                         <div className="text-foreground">{game.venue_name}</div>
                         {game.venue_address && (
                           <div className="text-xs text-muted-foreground/80">
                             {game.venue_address}
                           </div>
                         )}
                       </div>
                     </div>
                   )}
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Users className="h-4 w-4 mr-2 text-primary" />
                    Min {game.minimum_players_required} players
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="text-sm font-medium mb-3 text-foreground">Teams</div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="flex flex-col items-center">
                      {game.home_team?.team_logo && (
                        <div className="w-8 h-8 mb-1">
                          <TeamLogo 
                            logoUrl={game.home_team.team_logo}
                            teamName={game.home_team_label}
                            size="sm"
                          />
                        </div>
                      )}
                      <div className="font-medium text-foreground text-center">{game.home_team_label}</div>
                      <div className="text-muted-foreground text-xs">Home</div>
                    </div>
                    <div className="text-center flex items-center justify-center">
                      <span className="text-muted-foreground font-bold">vs</span>
                    </div>
                    <div className="flex flex-col items-center">
                      {game.away_team?.team_logo && (
                        <div className="w-8 h-8 mb-1">
                          <TeamLogo 
                            logoUrl={game.away_team.team_logo}
                            teamName={game.away_team_label}
                            size="sm"
                          />
                        </div>
                      )}
                      <div className="font-medium text-foreground text-center">{game.away_team_label}</div>
                      <div className="text-muted-foreground text-xs">Away</div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGameClick(game);
                    }}
                    className="flex-1 bg-black text-white hover:bg-gray-800 border-black"
                    variant="outline"
                  >
                    View Details
                  </Button>
                  {(game.game_status === 'pending' || game.game_status === 'confirmed') && user && (
                    <div 
                      className="flex-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <PlayerJoinRequestButton
                        gameId={game.id}
                        isAlreadyRequested={false}
                        isGameLocked={false}
                        gameStatus={game.game_status}
                        homeTeamName={game.home_team_label || 'Home Team'}
                        awayTeamName={game.away_team_label || 'Away Team'}
                        className="w-full bg-sports-green text-black hover:bg-sports-green/90 font-bold"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

          {filteredGames.length === 0 && !error && (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold text-muted-foreground mb-2">No games found</h3>
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : 'No games are currently available'}
              </p>
              {user && (
                <Button 
                  onClick={() => navigate('/game-management')}
                  className="mt-4"
                  variant="outline"
                >
                  Create a Game
                </Button>
              )}
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold text-destructive mb-2">Error Loading Games</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={loadGames} variant="outline">
                Try Again
              </Button>
            </div>
          )}
        </div>
      </main>
      
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}

      <EnhancedGameDetailsModal
        game={selectedGame}
        isOpen={showGameDetails}
        onClose={() => {
          setShowGameDetails(false);
          setSelectedGame(null);
        }}
        onGameUpdate={loadGames}
      />
    </div>
  );
}